<template>
    <div
        class="modal fade"
        id="bestiaryCompendiumModal"
        tabindex="-1"
        aria-hidden="true"
        ref="modal"
    >
        <div class="modal-dialog modal-xl modal-dialog-centered monster-modal">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title">
                        {{ mode === "add" ? "Add Monster" : "Edit Monster" }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>

                <div class="modal-body">
                    <!-- Edit: pick existing -->
                    <div v-if="mode === 'edit'" class="input-group mb-3">
                        <span class="input-group-text">Select Monster</span>
                        <select
                            v-model="selectedName"
                            @change="loadMonster"
                            class="form-select"
                        >
                            <option disabled value="">Select...</option>
                            <optgroup
                                v-for="(group, genus) in groupedBestiary"
                                :key="genus"
                                :label="genus"
                            >
                                <option
                                    v-for="m in group"
                                    :key="m.name"
                                    :value="m.name"
                                >
                                    {{ m.name }}
                                </option>
                            </optgroup>
                        </select>
                    </div>

                    <!-- Monster core fields -->
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Name</span>
                                <input
                                    v-model="monster.name"
                                    class="form-control"
                                />
                            </div>
                        </div>
                        <div class="col-md-6 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Genus</span>
                                <input
                                    v-model="monster.genus"
                                    class="form-control"
                                    list="genusList"
                                />
                                <datalist id="genusList">
                                    <option
                                        v-for="genus in uniqueGenus"
                                        :key="genus"
                                        :value="genus"
                                    />
                                </datalist>
                            </div>
                        </div>
                        <div class="col-sm-12">
                            <div class="input-group mb-3">
                                <span class="input-group-text"
                                    >Description</span
                                >
                                <textarea
                                    v-model="monster.description"
                                    class="form-control"
                                    rows="3"
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    <div class="row">
                        <div class="col-md-3 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">HP</span>
                                <input
                                    v-model.number="monster.hp"
                                    type="number"
                                    class="form-control"
                                />
                            </div>
                        </div>
                        <div class="col-md-3 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">CR</span>
                                <input
                                    v-model.number="monster.cr"
                                    type="number"
                                    class="form-control"
                                />
                            </div>
                        </div>
                        <div class="col-md-3 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Speed</span>
                                <input
                                    v-model="monster.speed"
                                    class="form-control"
                                />
                            </div>
                        </div>
                        <div class="col-md-3 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Size</span>
                                <input
                                    v-model="monster.size"
                                    class="form-control"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Attributes -->
                    <div class="row">
                        <div
                            class="col-md-2 mb-3"
                            v-for="stat in [
                                'grit',
                                'wit',
                                'smarts',
                                'heart',
                                'luck',
                                'strange',
                            ]"
                            :key="stat"
                        >
                            <div class="input-group">
                                <span
                                    class="input-group-text text-capitalize"
                                    >{{ stat }}</span
                                >
                                <input
                                    v-model.number="monster[stat]"
                                    type="number"
                                    class="form-control"
                                />
                            </div>
                        </div>
                    </div>

                    <!-- Type & Archetype -->
                    <div class="row">
                        <div class="col-md-6 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Type</span>
                                <select
                                    v-model="monster.type"
                                    class="form-control"
                                >
                                    <option
                                        v-for="(type, key) in monsterTypes"
                                        :key="key"
                                        :value="key"
                                    >
                                        {{ capitalize(key) }}
                                    </option>
                                </select>
                            </div>
                        </div>
                        <div class="col-md-6 mb-3">
                            <div class="input-group">
                                <span class="input-group-text">Archetype</span>
                                <select
                                    v-model="monster.archetype"
                                    class="form-select"
                                >
                                    <option value="grit">Grit</option>
                                    <option value="wit">Wit</option>
                                    <option value="smarts">Smarts</option>
                                    <option value="heart">Heart</option>
                                    <option value="luck">Luck</option>
                                    <option value="strange">Strange</option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Resistances, Weaknesses, Immunities -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Resistances</span>
                        <input
                            v-model="monster.resistances"
                            class="form-control"
                        />
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Weaknesses</span>
                        <input
                            v-model="monster.weaknesses"
                            class="form-control"
                        />
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Immunities</span>
                        <input
                            v-model="monster.immunities"
                            class="form-control"
                        />
                    </div>

                    <!-- Attacks -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Attacks</span>
                        <select v-model="selectedAttack" class="form-select">
                            <option disabled value="">Select...</option>
                            <option
                                v-for="atk in store.bestiaryAttacks.sort(
                                    (a, b) => a.name.localeCompare(b.name)
                                )"
                                :key="atk.name"
                                :value="atk.name"
                            >
                                {{ atk.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addAttack"
                        >
                            Add
                        </button>
                    </div>

                    <div
                        v-if="monster.attacks.length"
                        class="d-flex flex-wrap gap-2 mb-3"
                    >
                        <span
                            v-for="(atk, i) in monster.attacks"
                            :key="i"
                            class="badge bg-info d-flex align-items-center"
                        >
                            {{ atk }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeAttack(i)"
                            ></button>
                        </span>
                    </div>

                    <!-- Abilities -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Ability</span>
                        <select v-model="selectedAbility" class="form-select">
                            <option disabled value="">Select...</option>
                            <option
                                v-for="a in store.bestiaryAbilities.sort(
                                    (a, b) => a.name.localeCompare(b.name)
                                )"
                                :key="a.name"
                                :value="a.name"
                            >
                                {{ a.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addAbility"
                        >
                            Add
                        </button>
                    </div>

                    <div
                        v-if="monster.abilities.length"
                        class="d-flex flex-wrap gap-2"
                    >
                        <span
                            v-for="(ab, i) in monster.abilities"
                            :key="i"
                            class="badge bg-success d-flex align-items-center"
                        >
                            {{ ab }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeAbility(i)"
                            ></button>
                        </span>
                    </div>

                    <!-- Source -->
                    <div class="input-group mt-3">
                        <span class="input-group-text">Source</span>
                        <input v-model="monster.source" class="form-control" />
                    </div>
                </div>

                <p class="px-3">
                    <strong>
                        Note: These changes won't affect any monsters already in
                        encounters. Remove & re-add them for changes to take
                        effect.
                    </strong>
                </p>

                <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal">
                        <font-awesome-icon icon="multiply" class="me-1" /> Close
                    </button>
                    <button
                        v-if="mode === 'edit'"
                        class="btn btn-danger me-1"
                        @click="deleteMonster"
                    >
                        <font-awesome-icon icon="trash-alt" class="me-1" />
                        Delete
                    </button>
                    <button class="btn btn-primary" @click="saveMonster">
                        <font-awesome-icon icon="save" class="me-1" /> Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRosterStore } from "@/stores/index";

export default {
    name: "MonsterCompendiumModal",
    props: { mode: { type: String, default: "add" } },
    data() {
        return {
            store: useRosterStore(),
            selectedName: "",
            monster: this.blankMonster(),
            selectedAbility: "",
            selectedAttack: "",
        };
    },
    mounted() {
        this.$refs.modal.addEventListener("show.bs.modal", () => {
            this.resetForm();
        });
    },
    methods: {
        capitalize(str) {
            if (!str) return "";
            return str.charAt(0).toUpperCase() + str.slice(1);
        },
        blankMonster() {
            return {
                name: "",
                genus: "",
                description: "",
                hp: 0,
                cr: 0,
                speed: "",
                size: "",
                grit: 0,
                wit: 0,
                smarts: 0,
                heart: 0,
                luck: 0,
                strange: 0,
                attacks: [],
                abilities: [],
                immunities: "",
                resistances: "",
                weaknesses: "",
                type: "",
                archetype: "",
                source: "",
            };
        },
        resetForm() {
            this.selectedName = "";
            this.monster = this.blankMonster();
            this.selectedAbility = "";
            this.selectedAttack = "";
        },
        loadMonster() {
            const found = this.store.bestiary.find(
                (m) => m.name === this.selectedName
            );
            if (found) this.monster = JSON.parse(JSON.stringify(found));
        },
        saveMonster() {
            if (!this.monster.name.trim()) {
                alert("Monster must have a name!");
                return;
            }
            if (this.mode === "add") {
                this.store.bestiary.push(this.monster);
                alert(`Monster "${this.monster.name}" added!`);
            } else {
                const idx = this.store.bestiary.findIndex(
                    (m) => m.name === this.selectedName
                );
                if (idx !== -1)
                    this.store.bestiary.splice(idx, 1, this.monster);
                alert(`Monster "${this.monster.name}" updated!`);
            }
            this.resetForm();
        },
        deleteMonster() {
            if (!this.selectedName) return;
            if (confirm(`Delete monster "${this.selectedName}"?`)) {
                const idx = this.store.bestiary.findIndex(
                    (m) => m.name === this.selectedName
                );
                if (idx !== -1) this.store.bestiary.splice(idx, 1);
                alert(`Monster "${this.selectedName}" deleted!`);
            }
            this.resetForm();
        },
        addAbility() {
            if (!this.selectedAbility) return;
            if (!this.monster.abilities.includes(this.selectedAbility)) {
                this.monster.abilities.push(this.selectedAbility);
            }
            this.selectedAbility = "";
        },
        removeAbility(i) {
            this.monster.abilities.splice(i, 1);
        },
        addAttack() {
            if (!this.selectedAttack) return;
            if (!this.monster.attacks.includes(this.selectedAttack)) {
                this.monster.attacks.push(this.selectedAttack);
            }
            this.selectedAttack = "";
        },
        removeAttack(i) {
            this.monster.attacks.splice(i, 1);
        },
    },
    computed: {
        uniqueGenus() {
            const genusSet = new Set();
            this.store.bestiary.forEach((m) => {
                if (m.genus) genusSet.add(m.genus);
            });
            return Array.from(genusSet).sort((a, b) => a.localeCompare(b));
        },
        monsterTypes() {
            return this.store.monsterTypes;
        },
        groupedBestiary() {
            if (!this.store.bestiary) return {};

            // group by genus
            const groups = {};
            this.store.bestiary.forEach((monster) => {
                const genus = monster.genus || "Other";
                if (!groups[genus]) groups[genus] = [];
                groups[genus].push(monster);
            });

            // sort each group by name
            for (const genus in groups) {
                groups[genus].sort((a, b) => a.name.localeCompare(b.name));
            }

            // sort groups alphabetically by genus
            const sortedGroups = {};
            Object.keys(groups)
                .sort((a, b) => a.localeCompare(b))
                .forEach((genus) => {
                    sortedGroups[genus] = groups[genus];
                });

            return sortedGroups;
        },
    },
};
</script>

<style scoped>
.monster-modal {
    max-width: 95%; /* or any value you like */
}
</style>

